import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Product, Order, Customer, PaymentMethod, DeliveryAddress } from '@/types';
import { generateId } from '@/services/storageService';
import { formatCurrency, getDateInBrasiliaTimezone } from '@/lib/utils';
import { 
  ShoppingCart, 
  User, 
  Search, 
  CreditCard, 
  Trash2, 
  FileText, 
  Check, 
  X, 
  UserPlus, 
  Phone, 
  Printer,
  Receipt,
  Percent,
  Truck
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import ProductSelector from '@/components/products/ProductSelector';
import CustomerSelector from '@/components/pdv/CustomerSelector';
import PDVCart from '@/components/pdv/PDVCart';
import PaymentConfirmationDialog from '@/components/payment/PaymentConfirmationDialog';
import { syncOrderWithErp, generateGuid, calculateDiscountValue, getPedidoCompleto } from '@/services/orderApiService';
import type { PedidoCompleto } from '@/services/orderApiService';
import { API_CONFIG } from '@/config/apiConfig';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { validateDocument, formatDocument } from '@/services/customerApiService';
import { formatSaleReceiptForThermalPrinting, formatPedidoCompletoForThermalPrinting, printReceipt, checkPrintService } from '@/services/printService';
import PedidoPreviewDialog from '@/components/orders/PedidoPreviewDialog';
import OrderTypeSelector, { OrderTypeOption } from '@/components/pdv/OrderTypeSelector';
import DeliveryAddressForm from '@/components/delivery/DeliveryAddressForm';

const PDVPage: React.FC = () => {
  const { addOrder, paymentMethods, currentUser } = useApp();
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [documentInput, setDocumentInput] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [observations, setObservations] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [faturarAutomatico, setFaturarAutomatico] = useState(false);
  const [lastSyncedOrderId, setLastSyncedOrderId] = useState<number | null>(null);
  const [showDanfe, setShowDanfe] = useState(false);
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [isDiscountDialogOpen, setIsDiscountDialogOpen] = useState(false);
  
  // Estados de delivery
  const [orderType, setOrderType] = useState<OrderTypeOption>('dine-in');
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress | undefined>(undefined);
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [estimatedTime, setEstimatedTime] = useState<number>(30);
  const [deliveryNotes, setDeliveryNotes] = useState<string>('');

  const [pedidoPreviewOpen, setPedidoPreviewOpen] = useState(false);
  const [pedidoPreview, setPedidoPreview] = useState<PedidoCompleto | null>(null);
  const [pedidoPreviewLoading, setPedidoPreviewLoading] = useState(false);
  const [pedidoPreviewError, setPedidoPreviewError] = useState<string | null>(null);
  
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    API_CONFIG.initialize();
    createNewOrder();
  }, []);

  const createNewOrder = () => {
    const newOrder: Order = {
      id: generateId(),
      tableId: '', // PDV não usa mesa
      items: [],
      status: 'open',
      type: orderType, // Usar o tipo selecionado
      createdAt: getDateInBrasiliaTimezone(),
      updatedAt: getDateInBrasiliaTimezone(),
      totalAmount: 0,
      externalId: generateGuid(), // Gerar um GUID válido para esta venda
      observations: '',
      document: '',
      deliveryStatus: orderType === 'dine-in' ? undefined : 'pending',
    };
    setCurrentOrder(newOrder);
    setSelectedCustomer(null);
    setDocumentInput('');
    setObservations('');
    setLastSyncedOrderId(null);
    setShowDanfe(false);
    setDiscountValue('');
    setDeliveryAddress(undefined);
    setDeliveryFee(0);
    setEstimatedTime(30);
    setDeliveryNotes('');
    
    setIsProductSelectorOpen(false);
  };

  // Atualizar o tipo do pedido quando mudar
  const handleOrderTypeChange = (newType: OrderTypeOption) => {
    setOrderType(newType);
    if (currentOrder) {
      setCurrentOrder({
        ...currentOrder,
        type: newType,
        deliveryStatus: newType === 'dine-in' ? undefined : 'pending',
        deliveryAddress: newType === 'delivery' ? deliveryAddress : undefined,
        deliveryFee: newType === 'delivery' ? deliveryFee : undefined,
        estimatedDeliveryTime: newType === 'delivery' ? estimatedTime : undefined,
        deliveryNotes: newType === 'delivery' ? deliveryNotes : undefined,
        updatedAt: getDateInBrasiliaTimezone(),
      });
    }
  };

  // Atualizar endereço de entrega
  const handleDeliveryAddressChange = (address: DeliveryAddress) => {
    setDeliveryAddress(address);
    if (currentOrder && currentOrder.type === 'delivery') {
      setCurrentOrder({
        ...currentOrder,
        deliveryAddress: address,
        updatedAt: getDateInBrasiliaTimezone(),
      });
    }
  };

  // Atualizar taxa de entrega
  const handleDeliveryFeeChange = (fee: number) => {
    setDeliveryFee(fee);
    if (currentOrder && currentOrder.type === 'delivery') {
      const subtotal = currentOrder.items.reduce(
        (sum, item) => sum + (item.product.price * item.quantity), 
        0
      );
      setCurrentOrder({
        ...currentOrder,
        deliveryFee: fee,
        totalAmount: subtotal + fee - (currentOrder.discount || 0),
        updatedAt: getDateInBrasiliaTimezone(),
      });
    }
  };

  const handleAddProduct = async (product: Product) => {
    if (!currentOrder) return;
    
    const existingItemIndex = currentOrder.items.findIndex(
      item => item.productId === product.id
    );

    const updatedItems = [...currentOrder.items];
    
    if (existingItemIndex >= 0) {
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + 1
      };
    } else {
      updatedItems.push({
        id: generateId(),
        productId: product.id,
        product: product,
        quantity: 1,
        status: 'pending'
      });
    }
    
    const newTotal = updatedItems.reduce(
      (sum, item) => sum + (item.product.price * item.quantity), 
      0
    );
    
    const updatedOrder = {
      ...currentOrder,
      items: updatedItems,
      totalAmount: newTotal,
      updatedAt: getDateInBrasiliaTimezone(),
      syncStatus: 'pending' as const
    };
    
    setCurrentOrder(updatedOrder);
    
    // Sincronizar automaticamente se o pedido estiver pago
    if (updatedOrder.status === 'paid') {
      try {
        console.log('Sincronizando pedido após adição de produto no PDV:', updatedOrder.id);
        await syncOrderWithErp(
          updatedOrder, 
          paymentMethods, 
          [], // valores de pagamento vazios para sincronização de item
          currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1, 
          selectedCustomer?.clienteID || 0,
          false
        );
        
        toast({
          title: "Produto sincronizado",
          description: `${product.name} foi sincronizado com a retaguarda`,
        });
      } catch (error) {
        console.error("Erro ao sincronizar produto:", error);
        toast({
          title: "Erro na sincronização",
          description: "Produto adicionado localmente, mas houve erro na sincronização",
          variant: "destructive"
        });
      }
    }
    
    toast({
      title: "Produto adicionado",
      description: `${product.name} foi adicionado à venda`
    });
  };

  const handleRemoveItem = (itemId: string) => {
    if (!currentOrder) return;
    
    const updatedItems = currentOrder.items.filter(item => item.id !== itemId);
    
    const newTotal = updatedItems.reduce(
      (sum, item) => sum + (item.product.price * item.quantity), 
      0
    );
    
    setCurrentOrder({
      ...currentOrder,
      items: updatedItems,
      totalAmount: newTotal,
      updatedAt: getDateInBrasiliaTimezone()
    });
  };

  const handleUpdateItemQuantity = async (itemId: string, newQuantity: number) => {
    if (!currentOrder) return;
    
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }
    
    const updatedItems = currentOrder.items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          quantity: newQuantity
        };
      }
      return item;
    });
    
    const subtotal = updatedItems.reduce(
      (sum, item) => sum + (item.product.price * item.quantity), 
      0
    );
    
    let finalDiscount = 0;
    if (discountValue && parseFloat(discountValue) > 0) {
      const discountFloat = parseFloat(discountValue);
      if (discountType === 'percentage') {
        finalDiscount = (subtotal * discountFloat) / 100;
      } else {
        finalDiscount = Math.min(discountFloat, subtotal);
      }
    }
    
    const updatedOrder = {
      ...currentOrder,
      items: updatedItems,
      totalAmount: subtotal - finalDiscount,
      updatedAt: getDateInBrasiliaTimezone(),
      syncStatus: 'pending' as const
    };
    
    setCurrentOrder(updatedOrder);
    
    // Sincronizar automaticamente se o pedido estiver pago
    if (updatedOrder.status === 'paid') {
      try {
        console.log('Sincronizando pedido após atualização de quantidade no PDV:', updatedOrder.id);
        await syncOrderWithErp(
          updatedOrder, 
          paymentMethods, 
          [], // valores de pagamento vazios para sincronização de item
          currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1, 
          selectedCustomer?.clienteID || 0,
          false
        );
        
        toast({
          title: "Quantidade sincronizada",
          description: "Alteração sincronizada com a retaguarda",
        });
      } catch (error) {
        console.error("Erro ao sincronizar alteração:", error);
        toast({
          title: "Erro na sincronização",
          description: "Alteração salva localmente, mas houve erro na sincronização",
          variant: "destructive"
        });
      }
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    
    if (currentOrder) {
      setCurrentOrder({
        ...currentOrder,
        customerId: customer.id,
        customerName: customer.name,
        document: customer.document || documentInput || ''
      });
      
      if (customer.document) {
        setDocumentInput(customer.document);
      }
      
      toast({
        title: "Cliente selecionado",
        description: `${customer.name} foi adicionado à venda`
      });
    }
  };

  const calculateOrderTotal = () => {
    if (!currentOrder || !currentOrder.items.length) return 0;
    
    const subtotal = currentOrder.items.reduce(
      (sum, item) => sum + (item.product.price * item.quantity), 
      0
    );
    
    let finalDiscount = 0;
    if (discountValue && parseFloat(discountValue) > 0) {
      const discountFloat = parseFloat(discountValue);
      if (discountType === 'percentage') {
        finalDiscount = (subtotal * discountFloat) / 100;
      } else {
        finalDiscount = Math.min(discountFloat, subtotal);
      }
    }
    
    return subtotal - finalDiscount;
  };
  
  useEffect(() => {
    if (currentOrder) {
      const newTotal = calculateOrderTotal();
      setCurrentOrder({
        ...currentOrder,
        totalAmount: newTotal,
        discount: discountValue ? parseFloat(discountValue) : undefined,
        discountType: discountValue ? discountType : undefined,
        updatedAt: getDateInBrasiliaTimezone()
      });
    }
  }, [discountValue, discountType]);

  const handleConfirmPayment = async (order: Order, paymentMethods: PaymentMethod[], paymentValues: number[]) => {
    if (!currentOrder) return;

    if (!order.externalId) {
      order.externalId = generateGuid();
    }
    
    const finalOrder: Order = {
      ...order,
      customerId: selectedCustomer?.id,
      customerName: selectedCustomer?.name,
      customer: selectedCustomer || undefined,
      document: documentInput || selectedCustomer?.document || '',
      status: 'paid' as const,
      updatedAt: getDateInBrasiliaTimezone(),
      paymentMethodId: paymentMethods[0]?.id,
      observations: observations,
      discount: discountValue ? parseFloat(discountValue) : undefined,
      discountType: discountValue ? discountType : undefined
    };
    
    addOrder(finalOrder);
    
    try {
      API_CONFIG.initialize();
      
      const clienteID = selectedCustomer?.clienteID || 
                      (selectedCustomer?.id ? parseInt(selectedCustomer.id, 10) : 0);
                      
      const syncResult = await syncOrderWithErp(
        finalOrder, 
        paymentMethods, 
        paymentValues, 
        currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1, 
        clienteID,
        faturarAutomatico
      );
      
      if (syncResult > 0) {
        setLastSyncedOrderId(syncResult);
        
        toast({
          title: "Venda sincronizada",
          description: `Venda sincronizada com sucesso. ID: ${syncResult}${faturarAutomatico ? ' (com faturamento automático)' : ''}`,
        });
        
        if (faturarAutomatico) {
          setShowDanfe(true);
        } else {
          await handleOpenPedidoPreview(syncResult);
        }
      }
    } catch (error) {
      console.error("Erro ao sincronizar venda:", error);
      toast({
        title: "Erro na sincronização",
        description: `A venda foi salva localmente, mas houve um erro na sincronização.`,
        variant: "destructive"
      });
    }
    
    let discountMessage = '';
    if (discountValue && parseFloat(discountValue) > 0) {
      const discountAmount = calculateDiscountValue(finalOrder);
      discountMessage = `, Desconto: ${formatCurrency(discountAmount)}`;
    }
    
    toast({
      title: "Venda finalizada",
      description: `Valor total: ${formatCurrency(finalOrder.totalAmount)}${discountMessage}${
        order.changeAmount > 0 ? `, Troco: ${formatCurrency(order.changeAmount)}` : ''
      }`
    });

    if (faturarAutomatico) {
      handlePrintReceipt(finalOrder);
    }
    
    if (!faturarAutomatico) {
      createNewOrder();
      setIsProductSelectorOpen(false);
    }
    
    setIsPaymentDialogOpen(false);
  };

  const handlePrintReceipt = async (order: Order) => {
    try {
      setIsPrinting(true);
      
      const isServiceAvailable = await checkPrintService();
      
      if (!isServiceAvailable) {
        throw new Error("Serviço de impressão não disponível");
      }
      
      const receiptContent = formatSaleReceiptForThermalPrinting(order);
      const printResult = await printReceipt(receiptContent);
      
      if (printResult) {
        toast({
          title: "Recibo impresso",
          description: "O comprovante de venda foi enviado para impressão"
        });
      }
      return printResult;
    } catch (error) {
      console.error("Erro ao imprimir recibo:", error);
      toast({
        title: "Erro ao imprimir",
        description: error instanceof Error ? error.message : "Não foi possível imprimir o comprovante",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsPrinting(false);
    }
  };

  async function handleOpenPedidoPreview(vendaId: number) {
    setPedidoPreviewOpen(true);
    setPedidoPreviewLoading(true);
    setPedidoPreviewError(null);
    setPedidoPreview(null);
    try {
      const pedido = await getPedidoCompleto(vendaId);
      setPedidoPreview(pedido);
    } catch (err) {
      setPedidoPreviewError(err instanceof Error ? err.message : 'Não foi possível carregar o pedido.');
    } finally {
      setPedidoPreviewLoading(false);
    }
  }

  const handleProceedToPayment = () => {
    if (!currentOrder || currentOrder.items.length === 0) {
      toast({
        title: "Não é possível finalizar",
        description: "Adicione pelo menos um produto à venda",
        variant: "destructive"
      });
      return;
    }
    
    setIsPaymentDialogOpen(true);
  };

  const handleClearOrder = () => {
    if (!currentOrder || currentOrder.items.length === 0) return;
    
    createNewOrder();
    
    setIsProductSelectorOpen(false);
    
    toast({
      title: "Venda cancelada",
      description: "Todos os itens foram removidos"
    });
  };

  const handleOpenDocumentDialog = () => {
    setIsDocumentDialogOpen(true);
  };

  const handleDocumentSubmit = () => {
    if (documentInput && !validateDocument(documentInput)) {
      setDocumentError('Documento inválido. Verifique o CPF ou CNPJ digitado.');
      return;
    }
    
    if (currentOrder) {
      setCurrentOrder({
        ...currentOrder,
        document: documentInput
      });
      
      if (documentInput) {
        const formattedDoc = formatDocument(documentInput);
        setDocumentInput(formattedDoc);
        
        toast({
          title: "Documento adicionado",
          description: `Documento ${formattedDoc} adicionado à venda`
        });
      } else {
        toast({
          title: "Documento removido",
          description: `O documento foi removido da venda`
        });
      }
    }
    
    setIsDocumentDialogOpen(false);
    setDocumentError('');
  };

  const handleUpdateObservations = (value: string) => {
    setObservations(value);
    if (currentOrder) {
      setCurrentOrder({
        ...currentOrder,
        observations: value
      });
    }
  };

  const handleDanfeClose = () => {
    setShowDanfe(false);
    createNewOrder();
  };

  const handleOpenDiscountDialog = () => {
    setIsDiscountDialogOpen(true);
  };

  const handleManualSync = async () => {
    if (!currentOrder || currentOrder.items.length === 0) return;
    
    try {
      API_CONFIG.initialize();
      
      const clienteID = selectedCustomer?.clienteID || 
                      (selectedCustomer?.id ? parseInt(selectedCustomer.id, 10) : 0);
      
      console.log('Iniciando sincronização manual do pedido:', currentOrder.id);
      
      const syncResult = await syncOrderWithErp(
        currentOrder, 
        paymentMethods, 
        [], // valores de pagamento vazios para sincronização manual
        currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1, 
        clienteID,
        false // não faturar automaticamente na sincronização manual
      );
      
      if (syncResult > 0) {
        // Atualizar o status de sincronização
        const updatedOrder = {
          ...currentOrder,
          syncStatus: 'synced' as const,
          updatedAt: getDateInBrasiliaTimezone()
        };
        setCurrentOrder(updatedOrder);
        
        toast({
          title: "Sincronização realizada",
          description: `Pedido sincronizado com sucesso. ID: ${syncResult}`,
        });
      }
    } catch (error) {
      console.error("Erro na sincronização manual:", error);
      toast({
        title: "Erro na sincronização",
        description: "Não foi possível sincronizar com a retaguarda. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  const handleDiscountSubmit = () => {
    if (!currentOrder) return;
    
    const discountFloat = parseFloat(discountValue || '0');
    
    if (discountFloat < 0) {
      toast({
        title: "Valor inválido",
        description: "O desconto não pode ser negativo",
        variant: "destructive"
      });
      return;
    }
    
    if (discountType === 'percentage' && discountFloat > 100) {
      toast({
        title: "Valor inválido",
        description: "O desconto percentual não pode ser maior que 100%",
        variant: "destructive"
      });
      return;
    }
    
    const subtotal = currentOrder.items.reduce(
      (sum, item) => sum + (item.product.price * item.quantity), 
      0
    );
    
    if (discountType === 'fixed' && discountFloat > subtotal) {
      toast({
        title: "Valor inválido",
        description: "O desconto não pode ser maior que o valor total",
        variant: "destructive"
      });
      return;
    }
    
    const newTotal = calculateOrderTotal();
    setCurrentOrder({
      ...currentOrder,
      totalAmount: newTotal,
      discount: discountFloat > 0 ? discountFloat : undefined,
      discountType: discountFloat > 0 ? discountType : undefined,
      updatedAt: getDateInBrasiliaTimezone()
    });
    
    if (discountFloat > 0) {
      const discountAmount = discountType === 'percentage' 
        ? (subtotal * discountFloat) / 100 
        : discountFloat;
        
      toast({
        title: "Desconto aplicado",
        description: `Desconto de ${discountType === 'percentage' ? `${discountFloat}%` : formatCurrency(discountFloat)} aplicado (${formatCurrency(discountAmount)})`
      });
    } else {
      toast({
        title: "Desconto removido",
        description: "O desconto foi removido da venda"
      });
    }
    
    setIsDiscountDialogOpen(false);
  };

  // Calcular total incluindo taxa de entrega
  const getTotalWithDelivery = () => {
    if (!currentOrder) return 0;
    const subtotal = currentOrder.totalAmount;
    return orderType === 'delivery' ? subtotal + deliveryFee : subtotal;
  };

  return (
    <div className="container mx-auto p-4 pb-20 bg-gradient-to-b from-purple-50 to-white">
      <h1 className="text-2xl font-bold mb-4 text-purple-800 flex items-center">
        <ShoppingCart className="mr-2 h-6 w-6 text-purple-700" /> 
        PDV - Ponto de Venda
      </h1>
      
      {/* Seletor de Tipo de Pedido */}
      <Card className="mb-4 border-purple-200 shadow-md">
        <CardContent className="pt-4 pb-3">
          <p className="text-sm font-medium text-gray-700 mb-2">Tipo do Pedido:</p>
          <OrderTypeSelector 
            value={orderType} 
            onChange={handleOrderTypeChange}
            disabled={currentOrder && currentOrder.items.length > 0}
          />
          {currentOrder && currentOrder.items.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Limpe os itens para alterar o tipo do pedido
            </p>
          )}
        </CardContent>
      </Card>
      
      {/* Card Cliente - visível apenas no mobile, aparece primeiro */}
      <div className="lg:hidden">
        <Card className="border-indigo-200 shadow-md bg-gradient-to-br from-indigo-50 to-white mb-4">
          <CardHeader className="pb-2 border-b border-indigo-100">
            <CardTitle className="text-lg text-indigo-800">Cliente</CardTitle>
          </CardHeader>
          <CardContent className="pt-3 pb-2">
            {selectedCustomer ? (
              <div className="flex items-center mb-2">
                <div className="bg-indigo-100 p-1.5 rounded-full mr-2">
                  <User className="h-4 w-4 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-indigo-800 text-xs">{selectedCustomer.name}</p>
                  {(documentInput || selectedCustomer.document) && (
                    <p className="text-xs text-indigo-500">
                      <FileText className="h-2.5 w-2.5 inline mr-1" />
                      {documentInput || selectedCustomer.document}
                    </p>
                  )}
                  {selectedCustomer.phone && (
                    <p className="text-xs text-indigo-400">
                      <Phone className="h-2.5 w-2.5 inline mr-1" />
                      {selectedCustomer.phone}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-indigo-50 p-2 rounded-lg text-center mb-2">
                <User className="mx-auto h-5 w-5 text-indigo-300 mb-0.5" />
                <p className="text-indigo-800 font-medium text-xs">Nenhum cliente selecionado</p>
                {documentInput && (
                  <p className="text-xs text-indigo-500 mt-0.5">
                    <FileText className="h-2.5 w-2.5 inline mr-1" />
                    Documento: {documentInput}
                  </p>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 w-full text-xs h-7"
                onClick={() => setIsCustomerSelectorOpen(true)}
              >
                <User className="h-3 w-3 mr-1" />
                Selecionar
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 w-full text-xs h-7"
                onClick={handleOpenDocumentDialog}
              >
                <FileText className="h-3 w-3 mr-1" />
                + Documento
              </Button>
            </div>

            <div className="mt-1.5">
              <p className="text-xs font-medium text-gray-700 mb-0.5">Observações:</p>
              <Textarea
                placeholder="Adicione observações para esta venda..."
                value={observations}
                onChange={(e) => handleUpdateObservations(e.target.value)}
                className="text-xs resize-none"
                rows={1}
              />
            </div>

            <div className="flex items-center justify-between mt-1.5">
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-gray-700">Desconto</div>
                {currentOrder?.discount ? (
                  <div className="text-xs text-green-600">
                    {currentOrder.discountType === 'percentage' 
                      ? `${currentOrder.discount}%` 
                      : formatCurrency(currentOrder.discount)}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Nenhum desconto aplicado</div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-green-300 text-green-700 hover:bg-green-50 h-7"
                onClick={handleOpenDiscountDialog}
              >
                <Percent className="h-3 w-3 mr-1" />
                {currentOrder?.discount ? 'Editar' : 'Aplicar'}
              </Button>
            </div>

            <div className="mt-1.5 flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-gray-700">Faturar</div>
                <div className="text-xs text-gray-500">Gerar faturamento automático</div>
              </div>
              <Switch
                checked={faturarAutomatico}
                onCheckedChange={setFaturarAutomatico}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Formulário de Endereço para Delivery */}
          {orderType === 'delivery' && (
            <DeliveryAddressForm
              address={deliveryAddress}
              onChange={handleDeliveryAddressChange}
              customer={selectedCustomer}
              deliveryFee={deliveryFee}
              onDeliveryFeeChange={handleDeliveryFeeChange}
              estimatedTime={estimatedTime}
              onEstimatedTimeChange={setEstimatedTime}
              deliveryNotes={deliveryNotes}
              onDeliveryNotesChange={setDeliveryNotes}
            />
          )}
          
          <Card className="h-full border-purple-200 shadow-md bg-gradient-to-br from-white to-purple-50">
            <CardHeader className="pb-2 border-b border-purple-100">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg text-purple-800">Produtos</CardTitle>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-xs h-8"
                  onClick={() => setIsProductSelectorOpen(!isProductSelectorOpen)}
                >
                  <Search className="h-4 w-4 mr-1" />
                  Buscar Produtos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isProductSelectorOpen ? (
                <div className="mb-4">
                  <ProductSelector 
                    onSelectProduct={handleAddProduct} 
                    onClose={() => setIsProductSelectorOpen(false)}
                  />
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="mx-auto h-16 w-16 mb-4 text-purple-300" />
                  <p className="text-lg text-purple-600">Clique em "Buscar Produtos" para adicionar itens à venda</p>
                  <p className="text-sm text-purple-400 mt-2">Ou selecione produtos pela categoria</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="lg:col-span-1">
          <div className="space-y-4">
            {/* Card Cliente - visível apenas no desktop */}
            <Card className="hidden lg:block border-indigo-200 shadow-md bg-gradient-to-br from-indigo-50 to-white">
              <CardHeader className="pb-2 border-b border-indigo-100">
                <CardTitle className="text-lg text-indigo-800">Cliente</CardTitle>
              </CardHeader>
              <CardContent className="pt-3 pb-2">
                {selectedCustomer ? (
                  <div className="flex items-center mb-2">
                    <div className="bg-indigo-100 p-1.5 rounded-full mr-2">
                      <User className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-indigo-800 text-xs">{selectedCustomer.name}</p>
                      {(documentInput || selectedCustomer.document) && (
                        <p className="text-xs text-indigo-500">
                          <FileText className="h-2.5 w-2.5 inline mr-1" />
                          {documentInput || selectedCustomer.document}
                        </p>
                      )}
                      {selectedCustomer.phone && (
                        <p className="text-xs text-indigo-400">
                          <Phone className="h-2.5 w-2.5 inline mr-1" />
                          {selectedCustomer.phone}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-indigo-50 p-2 rounded-lg text-center mb-2">
                    <User className="mx-auto h-5 w-5 text-indigo-300 mb-0.5" />
                    <p className="text-indigo-800 font-medium text-xs">Nenhum cliente selecionado</p>
                    {documentInput && (
                      <p className="text-xs text-indigo-500 mt-0.5">
                        <FileText className="h-2.5 w-2.5 inline mr-1" />
                        Documento: {documentInput}
                      </p>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 w-full text-xs h-7"
                    onClick={() => setIsCustomerSelectorOpen(true)}
                  >
                    <User className="h-3 w-3 mr-1" />
                    Selecionar
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 w-full text-xs h-7"
                    onClick={handleOpenDocumentDialog}
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    + Documento
                  </Button>
                </div>

                <div className="mt-1.5">
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Observações:</p>
                  <Textarea
                    placeholder="Adicione observações para esta venda..."
                    value={observations}
                    onChange={(e) => handleUpdateObservations(e.target.value)}
                    className="text-xs resize-none"
                    rows={1}
                  />
                </div>

                <div className="flex items-center justify-between mt-1.5">
                  <div className="space-y-0.5">
                    <div className="text-xs font-medium text-gray-700">Desconto</div>
                    {currentOrder?.discount ? (
                      <div className="text-xs text-green-600">
                        {currentOrder.discountType === 'percentage' 
                          ? `${currentOrder.discount}%` 
                          : formatCurrency(currentOrder.discount)}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">Nenhum desconto aplicado</div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-green-300 text-green-700 hover:bg-green-50 h-7"
                    onClick={handleOpenDiscountDialog}
                  >
                    <Percent className="h-3 w-3 mr-1" />
                    {currentOrder?.discount ? 'Editar' : 'Aplicar'}
                  </Button>
                </div>

                <div className="mt-1.5 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="text-xs font-medium text-gray-700">Faturar</div>
                    <div className="text-xs text-gray-500">Gerar faturamento automático</div>
                  </div>
                  <Switch
                    checked={faturarAutomatico}
                    onCheckedChange={setFaturarAutomatico}
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card className="flex flex-col border-blue-200 shadow-md bg-gradient-to-br from-blue-50 to-white overflow-hidden" style={{ maxHeight: "calc(100vh - 12rem)" }}>
              <CardHeader className="pb-2 border-b border-blue-100">
                <CardTitle className="text-lg text-blue-800">Itens da Venda</CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-hidden" style={{ height: "calc(100vh - 18rem)" }}>
                {currentOrder && (
                  <PDVCart 
                    items={currentOrder.items}
                    onUpdateQuantity={handleUpdateItemQuantity}
                    onRemoveItem={handleRemoveItem}
                    showDanfe={showDanfe}
                    orderId={lastSyncedOrderId}
                    onDanfeClose={handleDanfeClose}
                  />
                )}
              </CardContent>
              <CardFooter className="flex-col border-t border-blue-100 pt-4 bg-blue-50">
                <div className="flex justify-between w-full mb-4">
                  <span className="text-lg font-medium text-blue-800">Total:</span>
                  <span className="text-xl font-bold text-blue-700">
                    {currentOrder ? formatCurrency(currentOrder.totalAmount) : formatCurrency(0)}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 w-full">
                  <Button 
                    variant="outline" 
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    onClick={handleClearOrder}
                    disabled={!currentOrder || currentOrder.items.length === 0}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleProceedToPayment}
                    disabled={!currentOrder || currentOrder.items.length === 0}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Finalizar
                  </Button>
                </div>
                
                {/* Botão de sincronização manual */}
                {currentOrder && currentOrder.items.length > 0 && currentOrder.status === 'paid' && (
                  <Button 
                    variant="outline"
                    className="w-full mt-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                    onClick={handleManualSync}
                    disabled={!currentOrder || currentOrder.items.length === 0}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    Sincronizar com Retaguarda
                  </Button>
                )}

                {lastSyncedOrderId && (
                  <Button
                    variant="outline"
                    className="w-full mt-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                    onClick={() => handleOpenPedidoPreview(lastSyncedOrderId)}
                    disabled={pedidoPreviewLoading || isPrinting}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Imprimir pedido
                  </Button>
                )}
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
      
      {currentOrder && (
        <CustomerSelector
          onSelectCustomer={handleSelectCustomer}
          open={isCustomerSelectorOpen}
          onOpenChange={setIsCustomerSelectorOpen}
        />
      )}
      
      {currentOrder && (
        <PaymentConfirmationDialog
          order={currentOrder}
          open={isPaymentDialogOpen}
          onOpenChange={setIsPaymentDialogOpen}
          onConfirm={handleConfirmPayment}
        />
      )}

      <PedidoPreviewDialog
        open={pedidoPreviewOpen}
        onOpenChange={setPedidoPreviewOpen}
        pedido={pedidoPreview}
        isLoading={pedidoPreviewLoading}
        error={pedidoPreviewError}
      />

      <Dialog open={isDocumentDialogOpen} onOpenChange={setIsDocumentDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Adicionar Documento</DialogTitle>
            <DialogDescription>
              Adicione o CPF ou CNPJ para a nota fiscal
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="document" className="text-sm font-medium">
                CPF ou CNPJ
              </label>
              <Input
                id="document"
                value={documentInput}
                onChange={(e) => setDocumentInput(e.target.value)}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                className={documentError ? "border-red-400" : ""}
              />
              {documentError && (
                <p className="text-xs text-red-500">{documentError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDocumentDialogOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleDocumentSubmit}>
              <Check className="h-4 w-4 mr-2" />
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isDiscountDialogOpen} onOpenChange={setIsDiscountDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Aplicar Desconto</DialogTitle>
            <DialogDescription>
              Defina o tipo e valor do desconto a ser aplicado
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="discount-type" className="text-sm font-medium">
                Tipo de desconto
              </label>
              <Select 
                value={discountType} 
                onValueChange={(value) => setDiscountType(value as 'percentage' | 'fixed')}
              >
                <SelectTrigger id="discount-type">
                  <SelectValue placeholder="Selecione o tipo de desconto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentual (%)</SelectItem>
                  <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="discount-value" className="text-sm font-medium">
                {discountType === 'percentage' ? 'Percentual de desconto (%)' : 'Valor do desconto (R$)'}
              </label>
              <Input
                id="discount-value"
                type="number"
                step={discountType === 'percentage' ? '1' : '0.01'}
                min="0"
                max={discountType === 'percentage' ? '100' : undefined}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percentage' ? '0%' : 'R$ 0,00'}
              />
              <p className="text-xs text-gray-500">
                {discountType === 'percentage' 
                  ? 'Informe o percentual de 0 a 100%' 
                  : 'Informe o valor do desconto em reais'}
              </p>
              
              {currentOrder && currentOrder.items.length > 0 && discountValue && parseFloat(discountValue) > 0 && (
                <div className="mt-2 p-2 bg-green-50 border border-green-100 rounded-md">
                  <p className="text-xs font-medium text-green-800">Prévia do desconto</p>
                  
                  <div className="grid grid-cols-2 gap-1 mt-1 text-xs">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-right">
                      {formatCurrency(
                        currentOrder.items.reduce(
                          (sum, item) => sum + (item.product.price * item.quantity), 
                          0
                        )
                      )}
                    </span>
                    
                    <span className="text-green-600">Desconto:</span>
                    <span className="text-right text-green-600">
                      {discountType === 'percentage' 
                        ? `${discountValue}% (${formatCurrency(
                            (currentOrder.items.reduce(
                              (sum, item) => sum + (item.product.price * item.quantity),
                              0
                            ) * parseFloat(discountValue)) / 100
                          )})`
                        : formatCurrency(parseFloat(discountValue))
                      }
                    </span>
                    
                    <span className="text-gray-800 font-medium pt-1 border-t border-green-200">Total:</span>
                    <span className="text-right text-gray-800 font-medium pt-1 border-t border-green-200">
                      {formatCurrency(
                        discountType === 'percentage'
                          ? currentOrder.items.reduce(
                              (sum, item) => sum + (item.product.price * item.quantity), 
                              0
                            ) * (1 - parseFloat(discountValue) / 100)
                          : currentOrder.items.reduce(
                              (sum, item) => sum + (item.product.price * item.quantity), 
                              0
                            ) - parseFloat(discountValue)
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDiscountValue('');
                setIsDiscountDialogOpen(false);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Remover
            </Button>
            <Button onClick={handleDiscountSubmit}>
              <Check className="h-4 w-4 mr-2" />
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDVPage;
